import { Box, Stack } from '@mui/material';
import { compact } from 'lodash';
import { usePostHog } from 'posthog-js/react';

import { Avatar } from '@/components/Avatar';
import { Button } from '@/components/form/baseInputs/Button';
import { LinkButton } from '@/components/form/baseInputs/Link';
import { LogOut01Icon } from '@/components/icons/LogOut01Icon';
import { Settings01Icon } from '@/components/icons/Settings01Icon';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useLogoutUser } from '@/modules/authentication/authenticationUtils';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { getAppVersion } from '@/utils/environmentUtils';
import { getPosthogSessionURL } from '@/utils/posthogUtils';

function UserSettingsButtonLink() {
  return (
    <Button
      startIcon={Settings01Icon}
      size="sm"
      variant="secondary"
      href={getCompletePathFromRouteKey(ROUTE_KEYS.USER_SETTINGS, {})}
    >
      Manage profile
    </Button>
  );
}

function LogOutButtonLink() {
  const logoutUser = useLogoutUser();
  return (
    <Button
      endIcon={LogOut01Icon}
      onClick={logoutUser}
      size="sm"
      variant="destructive-primary"
      fullWidth
    >
      Log out
    </Button>
  );
}

export function UserSettingsTooltip() {
  const currentUser = useCurrentUser();
  const posthogClient = usePostHog();
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, createSuccessFeedback } = useFeedback();

  function handleCopyDebugDetails() {
    const posthogSessionURL = getPosthogSessionURL(posthogClient);
    const appURL = location.href;
    const version = getAppVersion();

    const details = compact([
      posthogSessionURL && `Posthog session URL: ${posthogSessionURL}`,
      `App Version: ${version}`,
      `App URL: ${appURL}`,
    ]);

    copyToClipboard(
      details.join('\n'),
      createSuccessFeedback('Copied to clipboard'),
      createErrorFeedback('Failed to copy to clipboard')
    );
  }

  const canAccessSettingsPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_USER_SETTINGS_PAGE
  );
  if (!currentUser) return null;
  return (
    <Stack spacing={2} p={2}>
      <Avatar
        initials={currentUser.initials}
        fullName={currentUser.displayName}
        email={currentUser.email}
        size="sm"
      />
      {currentUser.integrationLoginExpired && (
        <Callout severity="warning">
          Your integration access has expired. Please unlink and relink your
          integration account below via manage profile.
        </Callout>
      )}
      {canAccessSettingsPage && <UserSettingsButtonLink />}
      <LogOutButtonLink />
      {currentUser?.isInternalUser && (
        <Box>
          <LinkButton
            display="Copy debugging details (internal)"
            onClick={handleCopyDebugDetails}
          />
        </Box>
      )}
    </Stack>
  );
}
